import { EmbedPlayer } from "@/components/media/EmbedPlayer";
import { ResponsiveVideo } from "@/components/media/ResponsiveVideo";
import { useGeneralSettingsFormData } from "@/api/cms";
import { DEFAULT_PLAYER_SETTINGS } from "@/lib/media/embed";
import { cn } from "@/lib/utils";
import { Attachment } from "@/lib/types";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface MediaPreviewProps {
  attachment?: Attachment;
  previewImageUrl?: string;
  className?: string;
}

const IMAGE_PREVIEW_CLASS = "h-auto mt-3 w-1/2 mb-3";
const MOTION_PREVIEW_CLASS =
  "h-auto mt-3 w-full min-w-0 max-w-full mb-3 object-contain rounded-md overflow-hidden";

export const MediaPreview = ({
  attachment,
  previewImageUrl,
  className,
}: MediaPreviewProps) => {
  const { t } = useTranslation();
  const { data: playerSettings = DEFAULT_PLAYER_SETTINGS } =
    useGeneralSettingsFormData();

  if (!attachment && !previewImageUrl) return null;

  const kind = attachment?.kind ?? "image";
  const isMotionMedia = kind === "video" || kind === "embed";
  const baseClass = isMotionMedia ? MOTION_PREVIEW_CLASS : IMAGE_PREVIEW_CLASS;
  const resolvedClassName = cn(baseClass, className);

  if (kind === "video") {
    if (attachment?.videoPublicUrl) {
      return (
        <ResponsiveVideo
          className={resolvedClassName}
          src={attachment.videoPublicUrl}
          poster={attachment.posterPublicUrl ?? undefined}
          loop={playerSettings.playerLoop}
        />
      );
    }
    if (attachment?.videoStatus === "processing") {
      return (
        <div
          className={`${resolvedClassName} flex flex-col items-center justify-center gap-2 text-muted-foreground border border-dashed rounded-md p-4`}
        >
          <Loader2 className="w-8 h-8 animate-spin" />
          <p className="text-sm text-center">
            {t("mediaLibrary.video.processing")}
          </p>
        </div>
      );
    }
    if (attachment?.videoStatus === "error") {
      return (
        <p className={`text-sm text-destructive ${resolvedClassName}`}>
          {t("mediaLibrary.video.conversionError")}
        </p>
      );
    }
    if (previewImageUrl) {
      return (
        <img
          className={resolvedClassName}
          src={previewImageUrl}
          alt="media couverture"
        />
      );
    }
    return null;
  }

  if (kind === "embed") {
    return (
      <EmbedPlayer
        className={resolvedClassName}
        provider={attachment?.embedProvider}
        videoId={attachment?.embedVideoId}
        embedUrl={attachment?.embedUrl}
        title={attachment?.name ?? "embed preview"}
      />
    );
  }

  if (previewImageUrl) {
    return (
      <img
        className={resolvedClassName}
        src={previewImageUrl}
        alt="media couverture"
      />
    );
  }

  return null;
};
